import { useCallback, useState } from 'react';

import { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';

import { fetchVideoTaggingJobStatus, triggerVideoTaggingJob } from './api';
import { TaggingError } from './TaggingError';

type JobStatus = {
  SUCCESS: 'success';
  FAILED: 'failed';
};

export interface TagResponse {
  id: string;
  downloadUrl?: string;
  status: JobStatus[keyof JobStatus];
  filename: string;
}

export const POLLING_DELAY_TAGGING = 500;

export function useVideoTagging() {
  const [taggedFiles, setTaggedFiles] = useState({});

  // Poll status of tagging job.
  const fetchStatus = useCallback(
    (
      clipId: number,
      apiUrl: string,
      jwt: string,
      id: string,
      filename: string,
      videoFileId: string,
    ) => {
      return new Promise<string>((resolve, reject) => {
        let timer: number | null = null;

        fetchVideoTaggingJobStatus(clipId, apiUrl, jwt, id, filename)
          .then((response: TagResponse) => {
            if (response.status === 'success' && response.downloadUrl) {
              setTaggedFiles({ ...taggedFiles, [videoFileId]: response.downloadUrl });
              if (timer) {
                clearTimeout(timer);
              }
              resolve(response.downloadUrl);
            } else if (response.status === 'failed') {
              reject(new TaggingError(response, 'Received status is "failed".'));
            } else {
              timer = setTimeout(
                () =>
                  fetchStatus(clipId, apiUrl, jwt, id, filename, videoFileId)
                    .then((url) => resolve(url))
                    .catch((e) => reject(e)),
                POLLING_DELAY_TAGGING,
              );
            }
          })
          .catch((e) => reject(new TaggingError(e, 'Failed to fetch tagging job status.')));
      });
    },
    [taggedFiles],
  );

  // Trigger video tagging
  const tagVideo = useCallback(
    (apiUrl: string, jwt: string, clipId: number, videoFileId: string, flow?: string) => {
      return new Promise<string>((resolve, reject) => {
        const taggedFile = taggedFiles[videoFileId];
        if (taggedFile) {
          resolve(taggedFile);
        } else {
          triggerVideoTaggingJob(clipId, apiUrl, jwt, videoFileId, flow)
            .then((response: TagResponse) => {
              if (response.status === 'failed') {
                reject(new TaggingError(response, 'Received status is "failed".'));
              }
              if (response.downloadUrl) {
                setTaggedFiles({ ...taggedFiles, [videoFileId]: response.downloadUrl });
                resolve(response.downloadUrl);
              } else {
                fetchStatus(clipId, apiUrl, jwt, response.id, response.filename, videoFileId)
                  .then((url) => resolve(url))
                  .catch((e) => reject(new TaggingError(e, 'Failed to fetch job status.')));
              }
            })
            .catch((e) => reject(new TaggingError(e, 'Failed to trigger tagging job.')));
        }
      });
    },
    [fetchStatus, taggedFiles],
  );

  const shouldTagVideo = (file: VideoFile, location?: string) => {
    switch (location) {
      case 'svv': {
        return file.size && file.size <= 100 << 20;
      }
      default:
        return false;
    }
  };

  return { shouldTagVideo, tagVideo };
}
